/**
 * Artifacts API Client
 *
 * CRUD operations for user workspace artifacts (documents, emails, reports, data).
 */

import { api } from '../api';

// Types
export type ArtifactType = 'document' | 'email' | 'report' | 'data';
export type ArtifactStatus = 'draft' | 'ready' | 'sent' | 'archived';

export interface Citation {
  path: string;
  start_line: number;
  end_line: number;
  text: string;
}

export interface Artifact {
  id: string;
  user_id: string;
  artifact_type: ArtifactType;
  title: string;
  content: Record<string, unknown>;
  status: ArtifactStatus;
  source_message_id?: string | null;
  citations: Citation[];
  created_at: string;
  updated_at: string;
  access_level?: 'owner' | 'edit' | 'view';
}

export interface ArtifactResponse {
  artifact: Artifact;
}

export interface ArtifactListResponse {
  artifacts: Artifact[];
}

export interface CreateArtifactPayload {
  artifact_type: ArtifactType;
  title: string;
  content?: Record<string, unknown>;
  status?: ArtifactStatus;
  source_message_id?: string;
  citations?: Citation[];
}

export interface UpdateArtifactPayload {
  title?: string;
  content?: Record<string, unknown>;
  status?: ArtifactStatus;
  citations?: Citation[];
}

export interface DeleteArtifactResponse {
  deleted: boolean;
  id: string;
}

export interface ArtifactVersion {
  id: string;
  artifact_id: string;
  title: string;
  content_json: string;
  status: ArtifactStatus;
  created_at: string;
}

export interface ArtifactVersionListResponse {
  versions: ArtifactVersion[];
}

export interface EncodeSensitiveDataPayload {
  text: string;
  data_type: string;
  artifact_id?: string;
}

export interface EncodeSensitiveDataResponse {
  encoded_text: string;
  code: string;
}

// API Functions

/**
 * List artifacts for the current user.
 * Backend defaults to owned artifacts when scope is not provided.
 * 
 * For now, lets just load owned artifacts, but in the future we can add support for shared/project/org scopes as needed.
 * 
 */
export const listArtifacts = async (params?: {
  type?: ArtifactType;
  status?: ArtifactStatus;
}): Promise<Artifact[]> => {
  const searchParams: Record<string, string> = {
    scope: 'all', // Include owned + shared + project + org for enterprise visibility
  };
  if (params?.type) searchParams.type = params.type;
  if (params?.status) searchParams.status = params.status;

  const response = await api.get('api/artifacts', { searchParams }).json<ArtifactListResponse>();

  return response.artifacts;
};

/**
 * Get a specific artifact by ID.
 */
export const getArtifact = async (artifactId: string): Promise<Artifact> => {
  const response = await api.get(`api/artifacts/${artifactId}`).json<ArtifactResponse>();

  return response.artifact;
};

/**
 * Create a new artifact.
 */
export const createArtifact = async (payload: CreateArtifactPayload): Promise<Artifact> => {
  const response = await api.post('api/artifacts', { json: payload }).json<ArtifactResponse>();

  return response.artifact;
};

/**
 * Update an existing artifact.
 */
export const updateArtifact = async (
  artifactId: string,
  payload: UpdateArtifactPayload,
): Promise<Artifact> => {
  const response = await api
    .put(`api/artifacts/${artifactId}`, { json: payload })
    .json<ArtifactResponse>();

  return response.artifact;
};

/**
 * Delete an artifact.
 */
export const deleteArtifact = async (artifactId: string): Promise<DeleteArtifactResponse> => {
  return api.delete(`api/artifacts/${artifactId}`).json<DeleteArtifactResponse>();
};

/**
 * List versions for an artifact.
 */
export const listArtifactVersions = async (artifactId: string): Promise<ArtifactVersion[]> => {
  const response = await api
    .get(`api/artifacts/${artifactId}/versions`)
    .json<ArtifactVersionListResponse>();
  return response.versions;
};

/**
 * Revert an artifact to a specific version.
 */
export const revertArtifactVersion = async (
  artifactId: string,
  versionId: string,
): Promise<Artifact> => {
  const response = await api
    .post(`api/artifacts/${artifactId}/revert`, { json: { version_id: versionId } })
    .json<ArtifactResponse>();
  return response.artifact;
};

/**
 * Encode selected text as sensitive data.
 * This will replace the text with a code and store the original value encrypted.
 */
export const encodeSensitiveData = async (
  payload: EncodeSensitiveDataPayload,
): Promise<EncodeSensitiveDataResponse> => {
  const response = await api
    .post('api/artifacts/encode-sensitive-data', { json: payload })
    .json<EncodeSensitiveDataResponse>();
  return response;
};
